import { gql } from "@/graphql/typed-gql"

/**
 * Everything the kid shell needs, in one round trip: who they are, what is left
 * unfinished, and what they could start. Deliberately narrow — the shell shows
 * one learner's own world and nothing else.
 *
 * Subjects come from `learnerSubjects` rather than the full list: same subjects,
 * ranked for this learner and flagged with whether they are worth showing up
 * front. See LearnerSubjects on the server.
 */
/**
 * The learner's own profile: the same picture a grown-up gets on /students/:id,
 * minus everything they are not allowed to change. One round trip, because the
 * page is one screen.
 */
export const GET_KID_PROFILE = gql`
  query KidProfile($id: ID!) {
    student(id: $id) {
      id
      name
      age
      avatarUrl
      totalPoints
      currentStreak
      longestStreak
      lastActivityAt
      level {
        level
        name
        icon
        progressToNextLevel
        pointsToNextLevel
        nextLevel {
          name
        }
      }
      achievements {
        id
        achievementKey
        unlockedAt
      }
      proficiencies {
        id
        skillKey
        proficiency
        isUnlocked
        totalAttempts
        correctAttempts
        skill {
          id
          name
          grade
          subjectId
          subjectName
        }
      }
      sessions {
        id
        status
        createdAt
        completedAt
        subjectKey
        exerciseCount
        pointsEarned
        correctCount
        incorrectCount
        skippedCount
        totalCount
        subject {
          id
          name
          icon
        }
      }
    }
    subjects {
      id
      name
      icon
    }
    # Whether a letter is waiting, so the card can say so rather than making the
    # child open the page to find out.
    progressReportAvailability(studentId: $id) {
      allowed
      sessionsDone
      sessionsNeeded
      daysLeft
      availableAt
      lastReportAt
    }
  }
`

export const GET_KID_HOME = gql`
  query KidHome($id: ID!) {
    student(id: $id) {
      id
      name
      avatarUrl
      totalPoints
      currentStreak
      suggestedExerciseCount
      # A grown-up may cap one subject shorter than another, and the length
      # control above the grid is one control for all of them — so each tile has
      # to be able to say what it will actually serve.
      subjectSessionLengths {
        subjectKey
        count
        effectiveCount
      }
      # The home page draws the level as a bar towards the next one, so it needs
      # the distance as well as the name. The level itself was already being
      # fetched here and thrown away.
      level {
        name
        icon
        progressToNextLevel
        pointsToNextLevel
        nextLevel {
          name
        }
      }
    }
    sessions(studentId: $id) {
      id
      status
      subjectKey
      exerciseCount
      createdAt
      subject {
        id
        name
        icon
      }
    }
    learnerSubjects(studentId: $id) {
      id
      name
      icon
      available
      recommended
      lastSolvedAt
    }
  }
`
